// src/admin/AdminPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Avatar from "../schedule/Avatar";
import AvatarPicker from "./AvatarPicker";
import { getApiBase } from "../utils/getApiBase";
import {
  getEmployeeName,
  isPermanentEmployee,
  listPermanentEmployees,
  replacePermanentEmployees,
} from "../utils/employees";

// dnd-kit
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/* ===========================
   Stałe (trwałość theatre ID)
=========================== */
const LS_THEATRE = "planner:theatreCalendarId";

/* ===========================
   Pomocnicze
=========================== */
function normalizeEmp(emp) {
  if (typeof emp === "string") return { name: emp, isTheatre: false, email: "", phone: "" };
  return emp || { name: "", isTheatre: false, email: "", phone: "" };
}

function SortableRow({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style}>
      {typeof children === "function" ? children(listeners, attributes) : children}
    </div>
  );
}

/* ===========================
   AdminPage (dropdowny z Google)
=========================== */
const POSITIONS = [
  { key: 'reżyser', label: 'Reżyser' },
  { key: 'showcaller', label: 'Showcaller' },
  { key: 'stage_manager', label: 'Stage Manager' },
  { key: 'inspicjent', label: 'Inspicjent' },
];

export default function AdminPage({
  employees = [],
  updateEmployees,
  selectedCalendar,
  theatreCalendar,
  calendarList = [], // niewykorzystywane w dropdownach, zostawiamy dla zgodności
  changeCalendarAndReload,
  changeTheatreCalendar,
  eventsByDate = {},
  paymentRates = {},
  setPaymentRates = () => {},
  defaultFunctionRates = {},
  setDefaultFunctionRates = () => {},
  taxRate = 0,
  setTaxRate = () => {},
  userType = null, // 'user', 'admin', 'superAdmin'
  unavailableByDate = {},
  addUnavailableRange = () => 0,
}) {
  // Dodawanie nowej osoby
  const [newEmployee, setNewEmployee] = useState("");
  const [newEmployeeEmail, setNewEmployeeEmail] = useState("");
  const [newEmployeePhone, setNewEmployeePhone] = useState("");

  // Stany UI (menu/rename/avatar)
  const [menuOpenIdx, setMenuOpenIdx] = useState(null);
  const [pickerIdx, setPickerIdx] = useState(null);
  const [renameIdx, setRenameIdx] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [contactIdx, setContactIdx] = useState(null);
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  
  // Stany dla edycji użytkownika (login, hasło, role)
  const [editUserIdx, setEditUserIdx] = useState(null);
  const [editLogin, setEditLogin] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  const [editIsSuperAdmin, setEditIsSuperAdmin] = useState(false);
  
  // Stan dla sekcji kwot
  const [showPaymentRates, setShowPaymentRates] = useState(false);
  const [showDefaultFunctionRates, setShowDefaultFunctionRates] = useState(false);

  // Stan dla zakładek
  const [activeTab, setActiveTab] = useState('employees'); // 'employees', 'unavailability', 'logs', 'agents'
  const [rangeEmployee, setRangeEmployee] = useState("");
  const [rangeFromDate, setRangeFromDate] = useState("");
  const [rangeToDate, setRangeToDate] = useState("");
  const [rangeMsg, setRangeMsg] = useState("");
  
  // Stan dla logów
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState('');
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState("");
  const [agentName, setAgentName] = useState("");
  const [agentDescription, setAgentDescription] = useState("");
  const [generatedApiKey, setGeneratedApiKey] = useState("");

  const permanentEmployees = useMemo(() => listPermanentEmployees(employees), [employees]);
  const permanentEntries = useMemo(
    () =>
      (employees || [])
        .map((emp, idx) => ({ emp, idx }))
        .filter(({ emp }) => isPermanentEmployee(emp)),
    [employees]
  );
  const employeeNames = useMemo(
    () =>
      permanentEmployees
        .map((emp) => getEmployeeName(emp))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, "pl", { sensitivity: "base" })),
    [permanentEmployees]
  );

  const unavailableEntries = useMemo(() => {
    const entries = [];
    Object.entries(unavailableByDate || {}).forEach(([date, list]) => {
      (Array.isArray(list) ? list : []).forEach((entry) => {
        const name = typeof entry === "string" ? entry : entry?.name;
        if (name) entries.push({ date, name });
      });
    });
    entries.sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return a.name.localeCompare(b.name, "pl", { sensitivity: "base" });
    });
    return entries;
  }, [unavailableByDate]);

  // refs do przycisków „…” i panelu menu (portal)
  const menuBtnRefs = useRef({});
  const menuPanelRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  // Stabilne ID dla wierszy (po indeksie)
  const ids = permanentEntries.map(({ idx }) => `emp-${idx}`);

  function onDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const from = Number(active.id.replace("emp-", ""));
    const to = Number(over.id.replace("emp-", ""));
    const fromPos = permanentEntries.findIndex((entry) => entry.idx === from);
    const toPos = permanentEntries.findIndex((entry) => entry.idx === to);
    if (fromPos < 0 || toPos < 0) return;
    const nextPermanents = arrayMove(permanentEmployees, fromPos, toPos);
    updateEmployees(replacePermanentEmployees(employees, nextPermanents));
  }

  /* ====== Akcje pracowników ====== */
  const handleAddEmployee = () => {
    const name = newEmployee.trim();
    const email = newEmployeeEmail.trim();
    const phone = newEmployeePhone.trim();
    if (!name) return;

    const exists = employees.some((e) =>
      typeof e === "string"
        ? e.trim().toLowerCase() === name.toLowerCase()
        : (e?.name || "").trim().toLowerCase() === name.toLowerCase()
    );
    if (exists) {
      setNewEmployee("");
      return;
    }
    updateEmployees([...employees, { name, isTheatre: false, email, phone }]);
    setNewEmployee("");
    setNewEmployeeEmail("");
    setNewEmployeePhone("");
  };

  const handleRemoveEmployee = (emp) => {
    const name = typeof emp === "string" ? emp : emp?.name;
    const next = employees.filter((e) =>
      typeof e === "string" ? e !== name : e?.name !== name
    );
    updateEmployees(next);
  };

  const toggleTheatreGroup = (idx) => {
    const next = [...employees];
    const cur = normalizeEmp(next[idx]);
    next[idx] = { ...cur, isTheatre: !cur.isTheatre };
    updateEmployees(next);
  };

  const patchEmployee = (idx, patch) => {
    const next = [...employees];
    const cur = normalizeEmp(next[idx]);
    next[idx] = { ...cur, ...patch };
    updateEmployees(next);
  };

  const openContactEdit = (idx) => {
    setRenameIdx(null);
    setPickerIdx(null);
    setEditUserIdx(null);
    const emp = normalizeEmp(employees[idx]);
    setContactIdx(idx);
    setContactEmail(emp.email || "");
    setContactPhone(emp.phone || "");
  };

  const closeContactEdit = () => {
    setContactIdx(null);
    setContactEmail("");
    setContactPhone("");
  };

  const saveContactData = () => {
    if (contactIdx == null) return;
    patchEmployee(contactIdx, {
      email: contactEmail.trim(),
      phone: contactPhone.trim(),
    });
    closeContactEdit();
  };

  /* ====== Zarządzanie użytkownikami (login, hasło, role) ====== */
  const openUserEdit = (idx) => {
    setRenameIdx(null);
    setPickerIdx(null);
    setContactIdx(null);
    const emp = normalizeEmp(employees[idx]);
    setEditUserIdx(idx);
    setEditLogin(emp.login || "");
    setEditPassword("");
    setEditIsAdmin(emp.isAdmin || false);
    setEditIsSuperAdmin(emp.isSuperAdmin || false);
  };

  const closeUserEdit = () => {
    setEditUserIdx(null);
    setEditLogin("");
    setEditPassword("");
    setEditIsAdmin(false);
    setEditIsSuperAdmin(false);
  };

    const saveUserData = async () => {
    if (editUserIdx === null) return;
    
    // Użyj oryginalnego obiektu z employees, nie normalizeEmp, aby zachować id
    const empRaw = employees[editUserIdx];
    const emp = typeof empRaw === 'string' ? { name: empRaw } : empRaw;
    
    // Jeśli podano hasło LUB login został zmieniony, zapisz przez API
    const loginChanged = editLogin.trim() && editLogin.trim() !== (emp.login || '');
    if (editPassword.trim() || loginChanged) {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        // Użyj id jeśli istnieje, w przeciwnym razie name
        const employeeId = emp.id || emp.name;
        
        const response = await fetch(`${getApiBase()}/api/setPassword`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            employeeId: employeeId,
            newPassword: editPassword.trim() || undefined, // Opcjonalne - tylko jeśli zostało podane
            login: editLogin.trim() || undefined, // Przekaż login jeśli został ustawiony
            isAdmin: editIsAdmin,
            isSuperAdmin: userType === 'superAdmin' ? editIsSuperAdmin : undefined, // Tylko jeśli super admin
            currentUserId: user.id || user.name,
            isAdmin: user.isAdmin || user.isSuperAdmin,
            currentUserIsSuperAdmin: user.isSuperAdmin || false, // Informacja czy aktualny użytkownik jest super adminem
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          alert(`Błąd ustawiania danych użytkownika: ${error.error || 'Nieznany błąd'}`);
          return;
        }
        
        // Po udanym zapisie, zaktualizuj lokalny stan z danymi zwróconymi z API
        // (ale NIE zapisuj do bazy - setPassword już to zrobił)
        const result = await response.json();
        if (result.employee) {
          // Zaktualizuj lokalny stan, zachowując istniejące pola (np. id, avatarUrl)
          const updatedEmp = { ...emp, ...result.employee };
          const next = [...employees];
          next[editUserIdx] = updatedEmp;
          // Użyj updateEmployees, ale najpierw upewnijmy się że passwordHash jest zachowany
          // Problem: updateEmployees zapisze do bazy i może nadpisać passwordHash
          // Rozwiązanie: najpierw pobierz aktualne dane z bazy (z passwordHash), potem zaktualizuj
          try {
            const loadResponse = await fetch(`${getApiBase()}/api/loadData`);
            if (loadResponse.ok) {
              const loadData = await loadResponse.json();
              const loadEmployees = loadData.employees || [];
              const loadEmp = loadEmployees.find(e => {
                const eObj = typeof e === 'string' ? { name: e } : e;
                return (eObj.id && eObj.id === emp.id) || (eObj.name && eObj.name === emp.name);
              });
              if (loadEmp) {
                const loadEmpObj = typeof loadEmp === 'string' ? { name: loadEmp } : loadEmp;
                // Połącz dane z bazy (z passwordHash) z danymi z API (z login/rolami)
                const finalEmp = { ...loadEmpObj, ...result.employee };
                const finalNext = [...employees];
                finalNext[editUserIdx] = finalEmp;
                updateEmployees(finalNext);
              } else {
                // Jeśli nie znaleziono, użyj danych z API
                updateEmployees(next);
              }
            } else {
              // Jeśli nie udało się załadować, użyj danych z API
              updateEmployees(next);
            }
          } catch (err) {
            // Jeśli błąd, użyj danych z API
            updateEmployees(next);
          }
        }
      } catch (error) {
        console.error('Błąd ustawiania danych użytkownika:', error);
        alert('Błąd ustawiania danych użytkownika');
        return;
      }
    } else {
      // Jeśli nie podano hasła, zapisz tylko login i role przez patchEmployee
      const updates = {
        login: editLogin.trim() || undefined,
        isAdmin: editIsAdmin,
        isSuperAdmin: userType === 'superAdmin' ? editIsSuperAdmin : (emp.isSuperAdmin || false),
      };
      Object.keys(updates).forEach(key => updates[key] === undefined && delete updates[key]);
      patchEmployee(editUserIdx, updates);
    }

    closeUserEdit();
  };

  /* ====== Zarządzanie kwotami ====== */
  const updatePaymentRate = (employeeName, position, amount) => {
    const next = { ...paymentRates };
    if (!next[employeeName]) {
      next[employeeName] = {};
    }
    const numAmount = parseFloat(amount) || 0;
    if (numAmount > 0) {
      next[employeeName][position] = numAmount;
    } else {
      delete next[employeeName][position];
      if (Object.keys(next[employeeName]).length === 0) {
        delete next[employeeName];
      }
    }
    setPaymentRates(next);
  };

  const getPaymentRate = (employeeName, position) => {
    return paymentRates[employeeName]?.[position] || 0;
  };

  /* ====== Domyślne kwoty za funkcje (oferowane klientom) ====== */
  const updateDefaultFunctionRate = (position, amount) => {
    const next = { ...defaultFunctionRates };
    const numAmount = parseFloat(amount) || 0;
    if (numAmount > 0) {
      next[position] = numAmount;
    } else {
      delete next[position];
    }
    setDefaultFunctionRates(next);
  };

  const getDefaultFunctionRate = (position) => {
    return defaultFunctionRates[position] || 0;
  };

  const openRename = (idx) => {
    setPickerIdx(null);
    setEditUserIdx(null);
    setContactIdx(null);
    const cur = normalizeEmp(employees[idx]);
    setRenameIdx(idx);
    setRenameValue(cur.name || "");
  };

  const submitRename = () => {
    if (renameIdx == null) return;
    const name = renameValue.trim();
    if (!name) {
      setRenameIdx(null);
      setRenameValue("");
      return;
    }
    // blokada duplikatów po nazwie
    const exists = employees.some((e, i) =>
      i !== renameIdx &&
      ((typeof e === "string" ? e : e?.name) || "").trim().toLowerCase() === name.toLowerCase()
    );
    if (exists) {
      setRenameIdx(null);
      setRenameValue("");
      return;
    }
    patchEmployee(renameIdx, { name });
    setRenameIdx(null);
    setRenameValue("");
  };

  // pozycjonowanie menu „…”
  const positionMenuFor = (idx) => {
    const btn = menuBtnRefs.current[idx];
    if (!btn) return;

    const r = btn.getBoundingClientRect();
    const width = 192; // w-48
    const gap = 8;

    // pozycja dla elementu fixed – BEZ scrollY/scrollX
    const vw = window.innerWidth || document.documentElement.clientWidth || 0;
    const left = Math.max(8, Math.min(vw - width - 8, r.right - width));
    const top = r.bottom + gap;

    setMenuPos({ top, left });
  };

  // zamykanie/repozycja menu po kliknięciu poza i ESC
  useEffect(() => {
    const onDown = (e) => {
      if (menuOpenIdx == null) return;
      const panel = menuPanelRef.current;
      const btn = menuBtnRefs.current[menuOpenIdx];
      if (
        panel &&
        !panel.contains(e.target) &&
        btn &&
        !btn.contains(e.target)
      ) {
        setMenuOpenIdx(null);
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setMenuOpenIdx(null);
    };
    const onReposition = () => {
      if (menuOpenIdx != null) positionMenuFor(menuOpenIdx);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);

    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [menuOpenIdx]);

  /* ===========================
     KALENDARZE z Google (dropdowny)
  ============================ */
  const [gLoading, setGLoading] = useState(false);
  const [gError, setGError] = useState("");
  const [gCalendars, setGCalendars] = useState([]); // [{id, summary, primary?, accessRole}]

  const fetchGoogleCalendars = async () => {
    setGLoading(true);
    setGError("");
    try {
      const tokenResp = await fetch(`${getApiBase()}/api/getAccessToken`).then((r) => r.json());
      const access_token = tokenResp?.access_token || "";
      if (!access_token) throw new Error("Brak tokenu dostępowego");

      const url = `https://www.googleapis.com/calendar/v3/users/me/calendarList?minAccessRole=reader`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${access_token}` },
      });
      if (!res.ok) {
        let t = "";
        try { t = await res.text(); } catch { }
        throw new Error(`${res.status} ${res.statusText}${t ? ` – ${t}` : ""}`);
      }
      const json = await res.json();
      const items = Array.isArray(json?.items) ? json.items : [];
      const mapped = items
        .map((i) => ({
          id: i.id || "",
          summary: i.summaryOverride || i.summary || i.id || "",
          primary: !!i.primary,
          accessRole: i.accessRole || "",
        }))
        .filter((c) => !!c.id);

      // sort: primary > rest, potem alfabetycznie
      mapped.sort((a, b) => {
        if (a.primary && !b.primary) return -1;
        if (!a.primary && b.primary) return 1;
        return (a.summary || "").localeCompare(b.summary || "", "pl", { sensitivity: "base" });
      });

      setGCalendars(mapped);
    } catch (e) {
      setGError(e?.message || String(e));
    } finally {
      setGLoading(false);
    }
  };

  useEffect(() => {
    fetchGoogleCalendars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pobierz logi systemowe
  const fetchSystemLogs = async () => {
    setLogsLoading(true);
    setLogsError('');
    try {
      const response = await fetch(`${getApiBase()}/api/getSystemLogs?limit=100`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      if (data.success && Array.isArray(data.logs)) {
        setLogs(data.logs);
      } else {
        throw new Error(data.error || 'Błąd pobierania logów');
      }
    } catch (error) {
      console.error('❌ [fetchSystemLogs] Error:', error);
      setLogsError(error?.message || 'Błąd pobierania logów');
    } finally {
      setLogsLoading(false);
    }
  };

  // Pobierz logi gdy zakładka Logi jest aktywna
  useEffect(() => {
    if (activeTab === 'logs') {
      fetchSystemLogs();
    }
  }, [activeTab]);

  const fetchHermesAgents = async () => {
    setAgentsLoading(true);
    setAgentsError("");
    try {
      const response = await fetch(`${getApiBase()}/api/hermesAgents`);
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || `HTTP ${response.status}`);
      }
      setAgents(Array.isArray(data.agents) ? data.agents : []);
    } catch (error) {
      setAgentsError(error?.message || "Błąd pobierania agentów");
    } finally {
      setAgentsLoading(false);
    }
  };

  const createHermesAgent = async () => {
    const name = String(agentName || "").trim();
    if (!name) return;
    setAgentsLoading(true);
    setAgentsError("");
    setGeneratedApiKey("");
    try {
      const response = await fetch(`${getApiBase()}/api/hermesAgents`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          description: String(agentDescription || "").trim(),
        }),
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || `HTTP ${response.status}`);
      }
      setGeneratedApiKey(data.apiKey || "");
      setAgentName("");
      setAgentDescription("");
      await fetchHermesAgents();
    } catch (error) {
      setAgentsError(error?.message || "Nie udało się utworzyć agenta");
    } finally {
      setAgentsLoading(false);
    }
  };

  const revokeAgent = async (agentId) => {
    if (!agentId) return;
    setAgentsLoading(true);
    setAgentsError("");
    try {
      const response = await fetch(`${getApiBase()}/api/hermesAgents`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "revoke",
          agentId,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || `HTTP ${response.status}`);
      }
      await fetchHermesAgents();
    } catch (error) {
      setAgentsError(error?.message || "Nie udało się dezaktywować agenta");
    } finally {
      setAgentsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'agents') {
      fetchHermesAgents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Fallback: jeśli aktualny wybór nie jest w pobranej liście, pokaż go w dropdownie
  const ensureInOptions = (list, currentId) => {
    if (!currentId) return list;
    if (list.some((c) => c.id === currentId)) return list;
    return [...list, { id: currentId, summary: `${currentId} (zapisany)` }];
  };

  const calendarsForMain = ensureInOptions(gCalendars, selectedCalendar);
  const calendarsForTheatre = ensureInOptions(gCalendars, theatreCalendar);

  const handleTheatreChange = (val) => {
    const v = String(val || "").trim();
    try { localStorage.setItem(LS_THEATRE, v); } catch { }
    changeTheatreCalendar(v);
  };

  // === Podsumowanie – teatr (Google Sheets) ===
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState("");
  const handleExportTheatre = async () => {
    setExportMsg("");
    setExporting(true);
    try {
      const r = await fetch(`${getApiBase()}/api/exportTheatreSummary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}), // opcjonalnie: { month: 'YYYY-MM' }
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error || `${r.status} ${r.statusText}`);
      if (j.sheets && Array.isArray(j.sheets)) {
        const sheetsList = j.sheets.map(s => `"${s.sheet}" (${s.rows} wierszy)`).join(', ');
        setExportMsg(`✅ Zaktualizowano ${j.total} zakładek: ${sheetsList}`);
      } else {
        // Fallback dla starego formatu (kompatybilność wsteczna)
        setExportMsg(`✅ Zaktualizowano arkusz "${j.sheet || j.sheets?.[0]?.sheet}" (${j.rows || j.sheets?.[0]?.rows || 0} wierszy)`);
      }
    } catch (e) {
      setExportMsg(`❌ Błąd: ${e?.message || String(e)}`);
    } finally {
      setExporting(false);
    }
  };

  // Formatuj timestamp na czytelną datę
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Brak daty';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return String(timestamp);
    }
  };

  // Formatuj typ akcji na czytelną nazwę
  const         formatActionType = (actionType) => {
    const types = {
      'login': 'Logowanie',
      'logout': 'Wylogowanie',
      'set_password': 'Ustawienie hasła',
      'change_role': 'Zmiana roli',
      'add_staff_to_event': 'Dodanie osoby do wydarzenia',
      'remove_staff_from_event': 'Usunięcie osoby z wydarzenia',
      'change_event_status': 'Zmiana statusu wydarzenia',
      'change_needed': 'Zmiana wartości needed',
      'edit_event_note': 'Edycja notatki',
      'edit_employee': 'Edycja danych pracownika',
      'change_email': 'Zmiana emaila',
    };
    return types[actionType] || actionType || 'Nieznana akcja';
  };

  // Formatuj szczegóły logu dla przypisań do wydarzeń
  const formatLogDetails = (log) => {
    if (log.actionType === 'add_staff_to_event' || log.actionType === 'remove_staff_from_event') {
      const details = log.details || {};
      const parts = [];
      if (details.addedStaff && details.addedStaff.length > 0) {
        parts.push(`Dodano: ${details.addedStaff.join(', ')}`);
      }
      if (details.removedStaff && details.removedStaff.length > 0) {
        parts.push(`Usunięto: ${details.removedStaff.join(', ')}`);
      }
      if (details.eventName) {
        parts.unshift(`Wydarzenie: ${details.eventName}`);
      }
      if (details.date) {
        parts.unshift(`Data: ${details.date}`);
      }
      return parts.join(' | ');
    }
    return null;
  };

  const handleAddUnavailableRange = () => {
    const personName = String(rangeEmployee || "").trim();
    const from = String(rangeFromDate || "").trim();
    const to = String(rangeToDate || "").trim();

    if (!personName || !from || !to) {
      setRangeMsg("Uzupełnij pracownika oraz obie daty zakresu.");
      return;
    }

    const addedDays = addUnavailableRange(personName, from, to);
    if (addedDays > 0) {
      setRangeMsg(`Dodano niedostępność dla "${personName}" na ${addedDays} ${addedDays === 1 ? "dzień" : "dni"}.`);
    } else {
      setRangeMsg(`Brak nowych wpisów — "${personName}" był już oznaczony w tym zakresie.`);
    }
  };

  return (
    <div className="text-white min-w-0 max-w-full overflow-x-hidden">
      <h2 className="text-xl font-bold mb-4">⚙️ Panel administracyjny</h2>

      {/* ZAKŁADKI — zawijają się / scrollują wewnątrz ramki, nie wychodzą poza panel */}
      <div
        data-testid="admin-tabs"
        className="flex flex-wrap gap-1 mb-6 border-b border-gray-700 max-w-full overflow-x-auto"
      >
        <button
          onClick={() => setActiveTab('employees')}
          className={`px-3 py-2 text-sm font-semibold whitespace-nowrap shrink-0 transition-colors ${
            activeTab === 'employees'
              ? 'border-b-2 border-blue-500 text-blue-400'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          👥 Pracownicy
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-3 py-2 text-sm font-semibold whitespace-nowrap shrink-0 transition-colors ${
            activeTab === 'logs'
              ? 'border-b-2 border-blue-500 text-blue-400'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          📋 Logi
        </button>
        <button
          onClick={() => setActiveTab('unavailability')}
          className={`px-3 py-2 text-sm font-semibold whitespace-nowrap shrink-0 transition-colors ${
            activeTab === 'unavailability'
              ? 'border-b-2 border-blue-500 text-blue-400'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          🚫 Niedostępności
        </button>
        {userType === 'superAdmin' && (
          <button
            onClick={() => setActiveTab('agents')}
            className={`px-3 py-2 text-sm font-semibold whitespace-nowrap shrink-0 transition-colors ${
              activeTab === 'agents'
                ? 'border-b-2 border-blue-500 text-blue-400'
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            🤖 Agenci API
          </button>
        )}
      </div>

      {/* ZAWARTOŚĆ ZAKŁADEK */}
      {activeTab === 'employees' && (
        <>

      {/* GŁÓWNY KALENDARZ (Dropdown z Google) */}
      <div className="mb-6">
        <label className="block font-semibold mb-2">📅 Wybierz kalendarz główny (Google):</label>

        {gLoading && (
          <div className="text-sm text-gray-400 mb-2">Ładowanie listy kalendarzy…</div>
        )}
        {gError && (
          <div className="text-sm text-red-400 mb-2">
            Błąd: {gError}{" "}
            <button
              onClick={fetchGoogleCalendars}
              className="underline text-blue-400 ml-2"
            >
              spróbuj ponownie
            </button>
          </div>
        )}

        <select
          value={selectedCalendar || ""}
          onChange={(e) => changeCalendarAndReload(e.target.value)}
          className="bg-gray-800 border border-gray-600 px-3 py-2 rounded w-full"
        >
          {!calendarsForMain.length && <option>Brak danych</option>}
          {calendarsForMain.map((cal) => (
            <option key={cal.id} value={cal.id}>
              {cal.summary}{cal.primary ? " (primary)" : ""}
            </option>
          ))}
        </select>
      </div>

      {/* KALENDARZ TEATRALNY (Dropdown z Google) */}
      <div className="mb-6">
        <label className="block font-semibold mb-2">🎭 Wybierz kalendarz teatralny (Google):</label>

        <select
          value={theatreCalendar || ""}
          onChange={(e) => handleTheatreChange(e.target.value)}
          onBlur={(e) => handleTheatreChange(e.target.value)}
          className="bg-gray-800 border border-gray-600 px-3 py-2 rounded w-full"
        >
          {!calendarsForTheatre.length && <option>Brak danych</option>}
          {calendarsForTheatre.map((cal) => (
            <option key={cal.id} value={cal.id}>
              {cal.summary}{cal.primary ? " (primary)" : ""}
            </option>
          ))}
        </select>
      </div>

      {/* Podsumowanie - teatr */}
      <div className="mb-6">
        <label className="block font-semibold mb-2">📄 Podsumowanie - teatr (Google Sheets)</label>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExportTheatre}
            disabled={exporting || !theatreCalendar}
            className={`px-4 py-2 rounded ${exporting || !theatreCalendar ? 'bg-gray-600' : 'bg-green-600 hover:bg-green-500'}`}
          >
            {exporting ? 'Przetwarzanie…' : 'Wyślij/aktualizuj arkusz'}
          </button>
          {!theatreCalendar && (
            <span className="text-sm text-gray-400">Wybierz najpierw kalendarz teatralny</span>
          )}
        </div>
        {exportMsg && (
          <div className={`mt-2 text-sm ${exportMsg.startsWith('✅') ? 'text-green-400' : 'text-red-400'}`}>
            {exportMsg}
          </div>
        )}
      </div>

      {/* DODAJ PRACOWNIKA */}
      <div className="mb-6">
        <label className="block font-semibold mb-2">➕ Dodaj pracownika:</label>
        <div className="grid gap-2 md:grid-cols-3">
          <input
            value={newEmployee}
            onChange={(e) => setNewEmployee(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddEmployee()}
            placeholder="Imię i nazwisko"
            className="bg-gray-900 border border-gray-700 rounded px-3 py-2"
          />
          <input
            value={newEmployeeEmail}
            onChange={(e) => setNewEmployeeEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddEmployee()}
            placeholder="Email (opcjonalnie)"
            className="bg-gray-900 border border-gray-700 rounded px-3 py-2"
          />
          <input
            value={newEmployeePhone}
            onChange={(e) => setNewEmployeePhone(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddEmployee()}
            placeholder="Telefon (opcjonalnie)"
            className="bg-gray-900 border border-gray-700 rounded px-3 py-2"
          />
        </div>
        <div className="mt-2">
          <button
            onClick={handleAddEmployee}
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-500"
          >
            Dodaj
          </button>
        </div>
      </div>

      {/* LISTA PRACOWNIKÓW (DND) */}
      <div className="mb-6">
        <label className="block font-semibold mb-2">👥 Lista pracowników:</label>

        <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {permanentEntries.map(({ emp, idx }) => {
                const itemId = `emp-${idx}`;
                const base = normalizeEmp(emp);
                const isTheatre = !!base.isTheatre;

                return (
                  <SortableRow key={itemId} id={itemId}>
                    {(listeners, attributes) => (
                      <div className="bg-gray-800 px-3 py-3 rounded border border-gray-700 min-w-0 overflow-hidden">
                        <div className="flex items-center justify-between gap-2 min-w-0">
                          {/* Lewa część: uchwyt + avatar + nazwa */}
                          <div className="flex items-center gap-3 min-w-0">
                            <button
                              {...listeners}
                              {...attributes}
                              className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-200 px-1 shrink-0"
                              title="Przeciągnij, aby zmienić kolejność"
                              aria-label="Przeciągnij"
                            >
                              ⠿
                            </button>

                            <Avatar employee={base} size={36} title={base.name} />
                            <span className="font-medium truncate min-w-0">{base.name}</span>
                          </div>

                          {/* Prawa część: Teatr + menu (portal) */}
                          <div className="flex items-center gap-2 shrink-0">
                            <label className="flex items-center gap-1 text-sm">
                              <input
                                type="checkbox"
                                checked={isTheatre}
                                onChange={() => toggleTheatreGroup(idx)}
                              />
                              🎭 Teatr
                            </label>

                            <button
                              ref={(el) => (menuBtnRefs.current[idx] = el)}
                              onClick={() => {
                                if (menuOpenIdx === idx) {
                                  setMenuOpenIdx(null);
                                } else {
                                  positionMenuFor(idx);
                                  setMenuOpenIdx(idx);
                                }
                              }}
                              className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600"
                            >
                              …
                            </button>
                          </div>
                        </div>

                        {/* Panel do wyboru avatara */}
                        {pickerIdx === idx && (
                          <div className="mt-3 border-t border-gray-700 pt-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <Avatar employee={base} size={44} title={base.name} />
                                <div className="text-sm text-gray-300">
                                  Dodaj/zmień zdjęcie dla <b>{base.name}</b>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setPickerIdx(null);
                                }}
                                className="px-3 py-2 bg-gray-700 rounded hover:bg-gray-600"
                              >
                                Zamknij
                              </button>
                            </div>

                            <div className="mt-3">
                              <AvatarPicker
                                employee={{ id: idx, ...base }}
                                onChange={(patch) => {
                                  patchEmployee(idx, patch); // { avatarUrl }
                                }}
                                onClear={() => {
                                  patchEmployee(idx, { avatarUrl: null });
                                }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Panel zmiany nazwy */}
                        {renameIdx === idx && (
                          <div className="mt-3 border-t border-gray-700 pt-3">
                            <div className="flex items-center gap-2">
                              <input
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                className="flex-1 bg-gray-900 border border-gray-700 rounded px-3 py-2"
                                placeholder="Nowa nazwa"
                                autoFocus
                              />
                              <button
                                onClick={submitRename}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded"
                              >
                                Zapisz
                              </button>
                              <button
                                onClick={() => {
                                  setRenameIdx(null);
                                  setRenameValue("");
                                }}
                                className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded"
                              >
                                Anuluj
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Panel edycji użytkownika (login, hasło, role) */}
                        {editUserIdx === idx && (
                          <div className="mt-3 border-t border-gray-700 pt-3 space-y-3">
                            <div>
                              <label className="block text-sm font-medium mb-1">Login:</label>
                              <input
                                type="text"
                                value={editLogin}
                                onChange={(e) => setEditLogin(e.target.value)}
                                className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2"
                                placeholder="login"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium mb-1">Nowe hasło (pozostaw puste, aby nie zmieniać):</label>
                              <input
                                type="password"
                                value={editPassword}
                                onChange={(e) => setEditPassword(e.target.value)}
                                className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2"
                                placeholder="Nowe hasło"
                              />
                            </div>

                            <div className="space-y-2">
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={editIsAdmin}
                                  onChange={(e) => setEditIsAdmin(e.target.checked)}
                                />
                                <span className="text-sm">Administrator</span>
                              </label>
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={editIsSuperAdmin}
                                  onChange={(e) => setEditIsSuperAdmin(e.target.checked)}
                                  disabled={!editIsAdmin || userType !== 'superAdmin'} // Super admin musi być też adminem, i tylko super admin może nadać uprawnienia super admina
                                />
                                <span className="text-sm">Super administrator</span>
                                {userType !== 'superAdmin' && (
                                  <span className="text-xs text-gray-500">(tylko super admin)</span>
                                )}
                              </label>
                            </div>

                            <div className="flex gap-2">
                              <button
                                onClick={saveUserData}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm"
                              >
                                Zapisz
                              </button>
                              <button
                                onClick={closeUserEdit}
                                className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded text-sm"
                              >
                                Anuluj
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Panel kontaktu (ukryty w dropdownie menu) */}
                        {contactIdx === idx && (
                          <div className="mt-3 border-t border-gray-700 pt-3 space-y-3">
                            <div>
                              <label className="block text-sm font-medium mb-1">Email:</label>
                              <input
                                type="email"
                                value={contactEmail}
                                onChange={(e) => setContactEmail(e.target.value)}
                                className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2"
                                placeholder="np. imie.nazwisko@firma.pl"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1">Telefon:</label>
                              <input
                                type="tel"
                                value={contactPhone}
                                onChange={(e) => setContactPhone(e.target.value)}
                                className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2"
                                placeholder="+48 600 000 000"
                              />
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={saveContactData}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm"
                              >
                                Zapisz kontakt
                              </button>
                              <button
                                onClick={closeContactEdit}
                                className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded text-sm"
                              >
                                Anuluj
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </SortableRow>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>

        {/* 🔧 Portal menu „…” */}
        {menuOpenIdx !== null &&
          createPortal(
            <div
              ref={menuPanelRef}
              className="fixed z-[9999] bg-gray-800 border border-gray-700 rounded shadow-lg w-48 overflow-hidden"
              style={{ top: menuPos.top, left: menuPos.left }}
            >
              <button
                onClick={() => {
                  setMenuOpenIdx(null);
                  openRename(menuOpenIdx);
                }}
                className="block w-full text-left px-3 py-2 hover:bg-gray-700 text-sm"
              >
                ✏️ Zmień nazwę
              </button>
              <button
                onClick={() => {
                  setMenuOpenIdx(null);
                  setPickerIdx(menuOpenIdx);
                }}
                className="block w-full text-left px-3 py-2 hover:bg-gray-700 text-sm"
              >
                🖼️ Zdjęcie / avatar
              </button>
              <button
                onClick={() => {
                  setMenuOpenIdx(null);
                  openUserEdit(menuOpenIdx);
                }}
                className="block w-full text-left px-3 py-2 hover:bg-gray-700 text-sm"
              >
                👤 Login / Hasło / Role
              </button>
              <button
                onClick={() => {
                  setMenuOpenIdx(null);
                  openContactEdit(menuOpenIdx);
                }}
                className="block w-full text-left px-3 py-2 hover:bg-gray-700 text-sm"
              >
                📇 Kontakt (email/tel)
              </button>
              <button
                onClick={() => {
                  const emp = employees[menuOpenIdx];
                  setMenuOpenIdx(null);
                  handleRemoveEmployee(emp);
                }}
                className="block w-full text-left px-3 py-2 hover:bg-gray-700 text-sm text-red-400"
              >
                ❌ Usuń
              </button>
            </div>,
            document.body
          )}
      </div>

      {/* Domyślne kwoty za funkcje (oferowane klientom) - tylko dla super administratorów */}
      {userType === 'superAdmin' && (
        <div className="mt-8 border-t border-gray-700 pt-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold">💰 Domyślne kwoty za funkcje (oferowane klientom)</h3>
              <p className="text-xs text-gray-400 mt-1">
                Te kwoty są używane do automatycznego obliczania wstępnej kwoty za wydarzenie
              </p>
            </div>
            <button
              onClick={() => setShowDefaultFunctionRates(!showDefaultFunctionRates)}
              className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-sm"
            >
              {showDefaultFunctionRates ? '▼ Ukryj' : '▶ Pokaż'}
            </button>
          </div>

          {showDefaultFunctionRates && (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2 px-3 font-medium">Funkcja</th>
                    <th className="text-right py-2 px-3 font-medium">Domyślna kwota (zł)</th>
                  </tr>
                </thead>
                <tbody>
                  {POSITIONS.map((pos) => (
                    <tr key={pos.key} className="border-b border-gray-800">
                      <td className="py-2 px-3">{pos.label}</td>
                      <td className="py-2 px-3">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={getDefaultFunctionRate(pos.key)}
                          onChange={(e) => updateDefaultFunctionRate(pos.key, e.target.value)}
                          placeholder="0.00"
                          className="w-full px-2 py-1 rounded border text-right
                                     bg-gray-800 border-gray-700
                                     text-gray-100
                                     focus:outline-none focus:border-blue-500"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500">
              Te kwoty będą używane do automatycznego obliczania sugerowanej kwoty za wydarzenie na podstawie liczby osób i ich funkcji.
            </p>
            
            {/* Procent podatku */}
            <div className="mt-4 pt-4 border-t border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Procent podatku (%)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={taxRate || ''}
                  onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  className="w-32 px-2 py-1 rounded border text-right
                             bg-gray-800 border-gray-700
                             text-gray-100
                             focus:outline-none focus:border-blue-500"
                />
              </div>
              <p className="text-xs text-gray-500">
                Procent podatku używany do obliczania różnicy w zakładce $$$ (suma wydarzeń - podatek - wynagrodzenia).
              </p>
            </div>
          </div>
          )}
        </div>
      )}

      {/* Sekcja kwot pracowników - tylko dla super administratorów */}
      {userType === 'superAdmin' && (
        <div className="mt-8 border-t border-gray-700 pt-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold">💰 Domyślne kwoty za stanowiska (dla pracowników)</h3>
              <p className="text-xs text-gray-400 mt-1">
                Kwoty dla poszczególnych pracowników na poszczególnych stanowiskach
              </p>
            </div>
            <button
              onClick={() => setShowPaymentRates(!showPaymentRates)}
              className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-sm"
            >
              {showPaymentRates ? '▼ Ukryj' : '▶ Pokaż'}
            </button>
          </div>

          {showPaymentRates && (
          <div className="space-y-4">
            {permanentEmployees.length === 0 ? (
              <p className="text-gray-400 text-sm">Brak pracowników</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-2 px-3 font-medium">Pracownik</th>
                      {POSITIONS.map((pos) => (
                        <th key={pos.key} className="text-right py-2 px-3 font-medium">
                          {pos.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {permanentEmployees.map((emp, idx) => {
                      const empName = typeof emp === 'string' ? emp : emp.name;
                      return (
                        <tr key={idx} className="border-b border-gray-800">
                          <td className="py-2 px-3">
                            <div className="flex items-center gap-2">
                              <Avatar employee={emp} size={24} />
                              <span>{empName}</span>
                            </div>
                          </td>
                          {POSITIONS.map((pos) => (
                            <td key={pos.key} className="py-2 px-3">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={getPaymentRate(empName, pos.key)}
                                onChange={(e) => updatePaymentRate(empName, pos.key, e.target.value)}
                                placeholder="0.00"
                                className="w-full px-2 py-1 rounded border
                                           bg-gray-800 border-gray-700
                                           text-gray-100 text-right
                                           focus:outline-none focus:border-blue-500"
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-xs text-gray-500">
              Kwoty są zapisywane automatycznie. Użyte będą do obliczania wynagrodzeń w zakładce $$$.
            </p>
          </div>
          )}
        </div>
      )}

      {/* Podsumowanie */}
      <div className="mt-8">
        <h3 className="font-semibold">📊 Podsumowanie</h3>
        <p className="text-gray-400">
          Wydarzeń wczytanych (dni): {Object.keys(eventsByDate || {}).length}
        </p>
        <p className="text-gray-400">Pracowników: {permanentEmployees.length}</p>
        <p className="text-gray-400">
          Teatralni: {permanentEmployees.filter((e) => typeof e === "object" && e.isTheatre).length}
        </p>
      </div>
        </>
      )}

      {activeTab === 'unavailability' && (
        <div className="space-y-6">
          <div className="bg-gray-800 border border-gray-700 rounded p-4">
            <h3 className="text-lg font-semibold mb-3">Dodaj niedostępność w zakresie dat</h3>
            <p className="text-sm text-gray-400 mb-4">
              Wybierz pracownika i zakres <b>od-do</b>. System dopisze niedostępność do każdego dnia z zakresu.
            </p>

            <div className="grid gap-3 md:grid-cols-3">
              <div>
                <label className="block text-sm font-medium mb-1">Pracownik</label>
                <select
                  value={rangeEmployee}
                  onChange={(e) => setRangeEmployee(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2"
                >
                  <option value="">Wybierz pracownika</option>
                  {employeeNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Od</label>
                <input
                  type="date"
                  value={rangeFromDate}
                  onChange={(e) => setRangeFromDate(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Do</label>
                <input
                  type="date"
                  value={rangeToDate}
                  onChange={(e) => setRangeToDate(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2"
                />
              </div>
            </div>

            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={handleAddUnavailableRange}
                className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-500"
              >
                Dodaj zakres
              </button>
              {rangeMsg && <span className="text-sm text-gray-300">{rangeMsg}</span>}
            </div>
          </div>

          <div className="bg-gray-800 border border-gray-700 rounded p-4">
            <h3 className="text-lg font-semibold mb-3">Aktualna lista niedostępności</h3>
            {unavailableEntries.length === 0 ? (
              <p className="text-gray-400 text-sm">Brak zapisanych niedostępności.</p>
            ) : (
              <div className="max-h-[420px] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-900">
                    <tr>
                      <th className="px-3 py-2 text-left">Data</th>
                      <th className="px-3 py-2 text-left">Pracownik</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unavailableEntries.map((entry, idx) => (
                      <tr key={`${entry.date}:${entry.name}:${idx}`} className="border-t border-gray-700">
                        <td className="px-3 py-2 text-gray-300">{entry.date}</td>
                        <td className="px-3 py-2">{entry.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'agents' && userType === 'superAdmin' && (
        <div className="space-y-6">
          <div className="bg-gray-800 border border-gray-700 rounded p-4">
            <h3 className="text-lg font-semibold mb-2">Dodaj agenta API</h3>
            <p className="text-sm text-gray-400 mb-4">
              Każdy agent dostaje osobny klucz API. Klucz jest pokazany tylko raz po wygenerowaniu.
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              <input
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                placeholder="Nazwa agenta (np. Hermes produkcja)"
                className="bg-gray-900 border border-gray-700 rounded px-3 py-2"
              />
              <input
                value={agentDescription}
                onChange={(e) => setAgentDescription(e.target.value)}
                placeholder="Opis (opcjonalnie)"
                className="bg-gray-900 border border-gray-700 rounded px-3 py-2"
              />
            </div>
            <div className="mt-3">
              <button
                type="button"
                onClick={createHermesAgent}
                disabled={agentsLoading || !agentName.trim()}
                className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed"
              >
                Generuj klucz API
              </button>
            </div>
            {generatedApiKey && (
              <div className="mt-4 p-3 rounded border border-yellow-600 bg-yellow-900/20">
                <div className="text-sm font-semibold text-yellow-300 mb-1">Nowy klucz API (skopiuj teraz):</div>
                <code className="text-xs break-all text-yellow-200">{generatedApiKey}</code>
              </div>
            )}
            {agentsError && (
              <div className="mt-3 text-sm text-red-400">{agentsError}</div>
            )}
          </div>

          <div className="bg-gray-800 border border-gray-700 rounded p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Lista agentów</h3>
              <button
                type="button"
                onClick={fetchHermesAgents}
                disabled={agentsLoading}
                className="px-3 py-2 rounded bg-gray-700 hover:bg-gray-600 disabled:bg-gray-600"
              >
                {agentsLoading ? "Ładowanie..." : "Odśwież"}
              </button>
            </div>

            {!agentsLoading && agents.length === 0 && (
              <p className="text-sm text-gray-400">Brak dodanych agentów.</p>
            )}

            <div className="space-y-2">
              {agents.map((agent) => (
                <div key={agent.id} className="border border-gray-700 rounded px-3 py-3 flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium">{agent.name}</div>
                    <div className="text-xs text-gray-400 mt-1">{agent.description || "Brak opisu"}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      id: {agent.id} · utworzono: {agent.createdAt || "-"} · ostatnie użycie: {agent.lastUsedAt || "-"}
                    </div>
                    <div className={`text-xs mt-1 ${agent.enabled ? "text-green-400" : "text-red-400"}`}>
                      {agent.enabled ? "Aktywny" : "Wyłączony"}
                    </div>
                  </div>
                  {agent.enabled && (
                    <button
                      type="button"
                      onClick={() => revokeAgent(agent.id)}
                      disabled={agentsLoading}
                      className="px-3 py-2 rounded bg-red-700 hover:bg-red-600 disabled:bg-gray-600 text-sm"
                    >
                      Dezaktywuj
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'logs' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">📋 Logi systemowe</h3>
            <button
              onClick={fetchSystemLogs}
              disabled={logsLoading}
              className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed"
            >
              {logsLoading ? 'Ładowanie...' : 'Odśwież'}
            </button>
          </div>

          {logsError && (
            <div className="mb-4 p-3 rounded bg-red-900 text-red-200">
              ❌ Błąd: {logsError}
            </div>
          )}

          {logsLoading && !logs.length && (
            <div className="text-center py-8 text-gray-400">
              Ładowanie logów...
            </div>
          )}

          {!logsLoading && logs.length === 0 && !logsError && (
            <div className="text-center py-8 text-gray-400">
              Brak logów w systemie
            </div>
          )}

          {logs.length > 0 && (
            <div className="bg-gray-800 rounded border border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-4 py-2 text-left">Data i czas</th>
                      <th className="px-4 py-2 text-left">Typ akcji</th>
                      <th className="px-4 py-2 text-left">Użytkownik</th>
                      <th className="px-4 py-2 text-left">Szczegóły</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id} className="border-t border-gray-700 hover:bg-gray-750">
                        <td className="px-4 py-2 text-gray-300">
                          {formatTimestamp(log.timestamp)}
                        </td>
                        <td className="px-4 py-2">
                          <span className="px-2 py-1 rounded bg-blue-900 text-blue-200 text-xs">
                            {formatActionType(log.actionType)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-300">
                          {log.userName || log.userId || 'Nieznany'}
                        </td>
                        <td className="px-4 py-2 text-gray-400">
                          {(() => {
                            const formattedDetails = formatLogDetails(log);
                            if (formattedDetails) {
                              return <div className="text-xs">{formattedDetails}</div>;
                            }
                            if (log.objectId) {
                              return (
                                <div className="text-xs">
                                  {log.objectId}
                                  {log.oldValue && log.newValue && (
                                    <span className="ml-2 text-gray-500">
                                      ({log.oldValue} → {log.newValue})
                                    </span>
                                  )}
                                </div>
                              );
                            }
                            if (log.details && typeof log.details === 'object') {
                              return (
                                <div className="text-xs mt-1 text-gray-500">
                                  {JSON.stringify(log.details, null, 2)}
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
